<script setup lang="ts">
import { reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import { ElMessage } from "element-plus";
import router from "../../router";
import axios from "axios";
import { useAuthStore } from "../../store/authStore";
import { isCompanyAdminFromToken } from "../../utils/authRole";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

const ensureAdminAccess = () => {
  if (!isCompanyAdminFromToken(localStorage.getItem("_panda"))) {
    router.replace("/request");
    return false;
  }
  return true;
};

if (!ensureAdminAccess()) {
  // Redirected in ensureAdminAccess.
}

interface InviteItem {
  key: number;
  name: string;
  email: string;
}
const formError = ref("");
const loading = ref(false);
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive({
  invites: [{ key: 0, name: "", email: "" }],
  inviteRole: "" as "" | "1" | "2",
});

const rules = reactive<FormRules<typeof ruleForm>>({
  inviteRole: [
    { required: true, message: "Please select a role", trigger: "change" },
  ],
  "invites.0.email": [
    {
      required: true,
      message: "Please enter an email address",
      trigger: "blur",
    },
    {
      type: "email" as const,
      message: "Please enter a valid email",
      trigger: "blur",
    },
  ],
});

const submitForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  formError.value = "";
  formEl.validate((valid) => {
    if (valid) {
      loading.value = true;
      postData();
    } else {
      formError.value = "Please check your inputs and try again.";
    }
  });
};

const postData = async () => {
  if (!ensureAdminAccess()) return;
  try {
    const role = Number(ruleForm.inviteRole);
    const response = await axios.post(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}adduser?token=${store.getToken}`,
      {
        email: ruleForm.invites[0].email,
        name: ruleForm.invites[0].name,
        company_id: store.getUserData.company_id,
        user_type: role,
      },
    );
    loading.value = false;
    if (response.status == 200) {
      if (response.data.status) {
        ElMessage({ message: "Invitation sent successfully", type: "success" });
        router.push("/account/team");
      } else {
        ElMessage({
          message:
            "Your session has expired. Kindy login to continue enjoying our services",
          type: "error",
        });
        logwat();
      }
    } else {
      ElMessage({ message: "Invitation failed, please retry.", type: "error" });
    }
  } catch {
    loading.value = false;
    formError.value = "Something went wrong, please try again.";
  }
};

const logwat = () => {
  store.setAuthStatus(false);
  store.$reset();
  localStorage.removeItem("_panda");
  router.push("/login");
};
</script>

<template>
  <div class="ac-card" v-loading="loading">
    <div class="ac-panel-head">
      <div>
        <h2 class="ac-panel-title">Invite a User</h2>
        <p class="ac-panel-desc">They'll receive an email to join your account</p>
      </div>
    </div>

    <div class="ac-card-body">
      <div class="ac-error" v-if="formError">{{ formError }}</div>

      <el-form
        ref="ruleFormRef"
        :model="ruleForm"
        :rules="rules"
        label-width="0px"
        status-icon
      >
        <template
          v-for="(invite, index) in ruleForm.invites"
          :key="invite.key"
        >
          <div class="ac-section-label">Account information</div>
          <div class="ac-grid">
            <div class="ac-field">
              <label class="ac-label" :for="`invite-name-${index}`">
                Name <span class="ac-optional">Optional</span>
              </label>
              <div class="ac-input-wrap">
                <input
                  :id="`invite-name-${index}`"
                  v-model="invite.name"
                  class="ac-input"
                  placeholder="Full name"
                />
                <span class="ac-input-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
              </div>
            </div>

            <el-form-item prop="invites.0.email" class="ac-fi">
              <div class="ac-field">
                <label class="ac-label" :for="`invite-email-${index}`">
                  Email Address <span class="ac-req">*</span>
                </label>
                <div class="ac-input-wrap">
                  <input
                    :id="`invite-email-${index}`"
                    v-model="invite.email"
                    class="ac-input"
                    type="email"
                    placeholder="user@company.com"
                  />
                  <span class="ac-input-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path
                        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                      />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </span>
                </div>
              </div>
            </el-form-item>
          </div>

          <div class="ac-section-label">Access level</div>
          <div class="ac-grid ac-grid--one">
            <el-form-item prop="inviteRole" class="ac-fi">
              <div class="ac-field">
                <label class="ac-label" for="invite-role">
                  Role <span class="ac-req">*</span>
                </label>
                <div class="ac-input-wrap">
                  <select
                    id="invite-role"
                    v-model="ruleForm.inviteRole"
                    class="ac-input"
                    required
                  >
                    <option disabled value="">-- Select --</option>
                    <option value="1">Normal</option>
                    <option value="2">Admin</option>
                  </select>
                  <span class="ac-input-icon">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </div>
            </el-form-item>
          </div>
        </template>

        <div class="ac-actions">
          <button class="ac-btn ac-btn--ghost" @click.prevent="router.push('/account/team')">
            ← Back to Users
          </button>
          <button
            class="ac-btn"
            @click.prevent="submitForm(ruleFormRef)"
            :disabled="loading"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            <span>{{ loading ? "Sending…" : "Send Invitation" }}</span>
          </button>
        </div>
      </el-form>
    </div>
  </div>
</template>
